/**
 * Âm thanh cho game "Giải cứu đại dương" — tạo hoàn toàn bằng Web Audio API.
 * Miễn phí, không cần file nhạc ngoài (tránh CSP/tải mạng), tôn trọng autoplay:
 * AudioContext chỉ tạo sau thao tác đầu tiên của người dùng.
 *
 * Ngoài hiệu ứng (SFX) còn có NHẠC NỀN dạng "ambient" dưới nước: một lớp pad trầm
 * êm + tiếng bong bóng ngẫu nhiên, lặp vô hạn cho tới khi dừng.
 */
export type OceanSfx = "click" | "correct" | "wrong" | "collect" | "finish" | "bubble" | "cheer";

type Ctx = AudioContext;

/** Âm lượng nhạc nền (đủ nhẹ để không lấn tiếng hiệu ứng). */
const AMBIENT_LEVEL = 0.12;

let ctx: Ctx | null = null;
let muted = false;

// Nút âm lượng tổng cho nhạc nền để bật/tắt mượt.
let ambientGain: GainNode | null = null;
let ambientNodes: AudioNode[] = [];
let bubbleTimer: ReturnType<typeof setInterval> | null = null;
let ambientOn = false;

function ensureCtx(): Ctx | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Phát 1 nốt đơn giản với bao hình lên/xuống mượt. */
function note(ac: Ctx, freq: number, startAt: number, dur: number, type: OscillatorType, peak: number) {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
  osc.connect(gain).connect(ac.destination);
  osc.start(startAt);
  osc.stop(startAt + dur + 0.02);
}

/** Nốt quét tần số — dùng cho "pop" bong bóng / nhặt rác. */
function sweep(ac: Ctx, from: number, to: number, startAt: number, dur: number, peak: number, type: OscillatorType = "sine") {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(from, startAt);
  osc.frequency.exponentialRampToValueAtTime(to, startAt + dur);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
  osc.connect(gain).connect(ac.destination);
  osc.start(startAt);
  osc.stop(startAt + dur + 0.02);
}

/** Một tiếng bong bóng ngắn (quét lên nhanh). */
function bubblePop(ac: Ctx, at: number, base: number, peak: number) {
  sweep(ac, base, base * 2.4, at, 0.14, peak, "sine");
}

export const oceanSound = {
  get muted() {
    return muted;
  },
  setMuted(v: boolean) {
    muted = v;
    if (ambientGain && ctx) {
      const target = v ? 0.0001 : AMBIENT_LEVEL;
      ambientGain.gain.cancelScheduledValues(ctx.currentTime);
      ambientGain.gain.exponentialRampToValueAtTime(target, ctx.currentTime + 0.3);
    }
  },
  toggle(): boolean {
    this.setMuted(!muted);
    return muted;
  },
  /** Gọi trong sự kiện click đầu tiên để "mở khóa" âm thanh trên trình duyệt. */
  unlock() {
    ensureCtx();
  },
  play(name: OceanSfx) {
    if (muted) return;
    const ac = ensureCtx();
    if (!ac) return;
    const t = ac.currentTime;
    switch (name) {
      case "click":
        note(ac, 340, t, 0.09, "triangle", 0.16);
        break;
      case "correct": // hợp âm rải trong trẻo (Đô–Mi–Son–Đô)
        note(ac, 523.25, t, 0.16, "sine", 0.2);
        note(ac, 659.25, t + 0.08, 0.16, "sine", 0.2);
        note(ac, 783.99, t + 0.16, 0.22, "sine", 0.22);
        bubblePop(ac, t + 0.22, 700, 0.14);
        break;
      case "wrong": // hai nốt đi xuống, hơi "buzz"
        note(ac, 300, t, 0.18, "sawtooth", 0.14);
        note(ac, 196, t + 0.14, 0.28, "sawtooth", 0.14);
        break;
      case "collect": // "pop" nhặt rác vào thùng + lấp lánh
        sweep(ac, 480, 1000, t, 0.16, 0.2);
        note(ac, 1174.7, t + 0.12, 0.14, "sine", 0.14);
        break;
      case "finish": // kèn chúc mừng vươn cao
        note(ac, 523.25, t, 0.16, "square", 0.14);
        note(ac, 659.25, t + 0.12, 0.16, "square", 0.14);
        note(ac, 783.99, t + 0.24, 0.16, "square", 0.14);
        note(ac, 1046.5, t + 0.36, 0.42, "square", 0.18);
        bubblePop(ac, t + 0.5, 820, 0.12);
        break;
      case "bubble":
        bubblePop(ac, t, 520, 0.12);
        break;
      case "cheer": // reo hò cổ vũ khi trả lời đúng: hợp âm vươn cao + lấp lánh
        note(ac, 523.25, t, 0.14, "sine", 0.22);
        note(ac, 659.25, t + 0.1, 0.14, "sine", 0.22);
        note(ac, 783.99, t + 0.2, 0.16, "sine", 0.22);
        note(ac, 1046.5, t + 0.3, 0.34, "square", 0.2);
        note(ac, 1318.51, t + 0.44, 0.14, "triangle", 0.16);
        note(ac, 1567.98, t + 0.56, 0.2, "triangle", 0.16);
        bubblePop(ac, t + 0.36, 900, 0.14);
        bubblePop(ac, t + 0.62, 1200, 0.12);
        break;
    }
  },

  /** Bật nhạc nền ambient dưới nước (idempotent). */
  startAmbient() {
    const ac = ensureCtx();
    if (!ac || ambientOn) return;
    ambientOn = true;

    const master = ac.createGain();
    master.gain.setValueAtTime(0.0001, ac.currentTime);
    master.gain.exponentialRampToValueAtTime(muted ? 0.0001 : AMBIENT_LEVEL, ac.currentTime + 1.2);
    master.connect(ac.destination);
    ambientGain = master;

    // Lọc thông thấp cho cảm giác "chìm dưới nước".
    const lp = ac.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(650, ac.currentTime);
    lp.connect(master);

    // LFO nhẹ đưa tần số cắt lên xuống → pad "thở".
    const lfo = ac.createOscillator();
    const lfoGain = ac.createGain();
    lfo.frequency.setValueAtTime(0.07, ac.currentTime);
    lfoGain.gain.setValueAtTime(180, ac.currentTime);
    lfo.connect(lfoGain).connect(lp.frequency);
    lfo.start();

    // Pad trầm: vài dao động detune tạo bè hòa âm A2–E3–A3.
    const pad = ac.createGain();
    pad.gain.setValueAtTime(0.5, ac.currentTime);
    pad.connect(lp);
    for (const freq of [110, 164.81, 220]) {
      const osc = ac.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, ac.currentTime);
      osc.detune.setValueAtTime((freq % 7) - 3, ac.currentTime);
      osc.connect(pad);
      osc.start();
      ambientNodes.push(osc);
    }
    ambientNodes.push(lfo, lfoGain, lp, pad);

    // Bong bóng ngẫu nhiên rải rác cho sinh động.
    bubbleTimer = setInterval(() => {
      if (muted || !ctx) return;
      if (Math.floor(ctx.currentTime * 1000) % 3 === 0) return; // thưa bớt
      bubblePop(ctx, ctx.currentTime + 0.02, 380 + (ctx.currentTime % 5) * 90, 0.05);
    }, 2600);
  },

  /** Dừng hẳn nhạc nền và giải phóng node. */
  stopAmbient() {
    if (!ambientOn) return;
    ambientOn = false;
    if (bubbleTimer) {
      clearInterval(bubbleTimer);
      bubbleTimer = null;
    }
    if (ctx && ambientGain) {
      ambientGain.gain.cancelScheduledValues(ctx.currentTime);
      ambientGain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    }
    const toStop = ambientNodes;
    ambientNodes = [];
    setTimeout(() => {
      for (const n of toStop) {
        try {
          if ("stop" in n && typeof (n as OscillatorNode).stop === "function") (n as OscillatorNode).stop();
          n.disconnect();
        } catch {
          /* node đã dừng */
        }
      }
      ambientGain?.disconnect();
      ambientGain = null;
    }, 500);
  },
};
